import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { formatDate } from '@angular/common';
import { Supplier } from '../_interfaces/supplier';
import { currencySymbol } from '../_pages/purchases/currencies';
import { INVOICE_LOGO } from './invoice.service';
import { AppSettingsService } from './app-settings.service';

// Generated client-side with pdfmake's browser bundle, same as
// InvoiceService for Sales - a NestJS backend trying to send this as a
// binary HTTP response ran into Buffer/Uint8Array + res.send() issues that
// the browser-side .open() approach sidesteps entirely.
//
// Used only if Settings > Company Settings hasn't been filled in yet (or
// the fetch fails) - the real values come from there (companyName /
// companyAddress / gstNo), since that's the one place this data is meant
// to be maintained. Web/Email/Tel aren't tracked there, so those stay fixed.
const PO_BUYER_FALLBACK = {
  name: 'Nustone Patios Limited',
  gbNumber: 'GB488764617',
  addressLines: ['Dobbies Lane, Marks Tey', 'Colchester, Essex - CO6 1EP'],
  country: 'United Kingdom',
  web: 'http://nustone.co.uk',
  email: 'singh@nustone.co.uk',
  phone: '+44 1206 700 599',
};

export interface PurchaseOrderPdfInput {
  reference: string;
  orderDate: string;
  expectedDeliveryDate: string | null;
  paymentTerms: string;
  incoterms: string;
  portOfLoading: string;
  portOfDischarge: string;
  notes: string;
  currency: string;
  supplier: Supplier | undefined;
  items: {
    sku: string;
    productName: string;
    quantity: number;
    unitCost: number;
    taxRate: number;
  }[];
  subtotal: number;
  taxTotal: number;
  grandTotal: number;
}

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderPdfService {
  constructor(private appSettingsService: AppSettingsService) {}

  print(po: PurchaseOrderPdfInput): void {
    this.appSettingsService.getCompanySettings().subscribe({
      next: (settings) => this.buildAndOpen(po, this.resolveBuyer(settings)),
      // Settings fetch failed - still let the PO print, just with the
      // fallback buyer details rather than blocking on it.
      error: () => this.buildAndOpen(po, PO_BUYER_FALLBACK),
    });
  }

  private resolveBuyer(settings: any) {
    const addressLines = String(settings?.companyAddress || '')
      .split(/\r?\n|,/)
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0);
    return {
      name: settings?.companyName || PO_BUYER_FALLBACK.name,
      gbNumber: settings?.gstNo || PO_BUYER_FALLBACK.gbNumber,
      addressLines: addressLines.length > 0 ? addressLines : PO_BUYER_FALLBACK.addressLines,
      country: PO_BUYER_FALLBACK.country,
      web: PO_BUYER_FALLBACK.web,
      email: PO_BUYER_FALLBACK.email,
      phone: PO_BUYER_FALLBACK.phone,
    };
  }

  private buildAndOpen(po: PurchaseOrderPdfInput, buyer: typeof PO_BUYER_FALLBACK): void {
    const symbol = currencySymbol(po.currency);
    const money = (value: number) => `${symbol}${Number(value || 0).toFixed(2)}`;
    const formatDateOrDash = (value: string | null) =>
      value ? formatDate(value, 'dd/MM/yyyy', 'en-US') : '-';

    const supplier = po.supplier;
    const supplierAddressLine = [supplier?.city, supplier?.postcode].filter(Boolean).join(' - ');

    const boxLayout = {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#999999',
      vLineColor: () => '#999999',
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 6,
      paddingBottom: () => 6,
    };

    const dd: any = {
      pageMargins: [40, 40, 40, 40],
      content: [
        {
          columns: [
            {
              width: '50%',
              stack: [
                { image: INVOICE_LOGO, width: 130 },
                { text: buyer.gbNumber, bold: true, fontSize: 10, margin: [2, 6, 0, 0] },
              ],
            },
            {
              width: '50%',
              stack: [
                { text: 'Purchase Order', style: 'header', alignment: 'right' },
                {
                  margin: [0, 8, 0, 0],
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'DATE', style: 'tableHeader', alignment: 'center' },
                        { text: 'PO No.', style: 'tableHeader', alignment: 'center' },
                      ],
                      [
                        { text: formatDateOrDash(po.orderDate), alignment: 'center' },
                        { text: po.reference, alignment: 'center' },
                      ],
                    ],
                  },
                  layout: boxLayout,
                },
              ],
            },
          ],
        },
        {
          margin: [0, 20, 0, 0],
          columns: [
            {
              width: '48%',
              table: {
                widths: ['*'],
                body: [
                  [{ text: 'BUYER', bold: true, fontSize: 10 }],
                  [
                    {
                      fontSize: 9,
                      stack: [
                        { text: buyer.name, bold: true, margin: [0, 0, 0, 2] },
                        ...buyer.addressLines.map((line) => ({ text: line })),
                        { text: buyer.country },
                        { text: `Web: ${buyer.web}` },
                        { text: `Email: ${buyer.email}` },
                        { text: `Tel: ${buyer.phone}` },
                      ],
                    },
                  ],
                ],
              },
              layout: boxLayout,
            },
            { width: '4%', text: '' },
            {
              width: '48%',
              table: {
                widths: ['*'],
                body: [
                  [{ text: 'VENDOR', bold: true, fontSize: 10 }],
                  [
                    {
                      fontSize: 9,
                      stack: [
                        { text: supplier?.name || '-', bold: true, margin: [0, 0, 0, 2] },
                        ...(supplier?.addressLine1 ? [{ text: supplier.addressLine1 }] : []),
                        ...(supplier?.addressLine2 ? [{ text: supplier.addressLine2 }] : []),
                        ...(supplierAddressLine ? [{ text: supplierAddressLine }] : []),
                        { text: supplier?.country || 'United Kingdom' },
                        { text: `Web: ${supplier?.website || ''}` },
                        { text: `Email: ${supplier?.email || ''}` },
                        { text: `Tel: ${supplier?.phone || ''}` },
                      ],
                    },
                  ],
                ],
              },
              layout: boxLayout,
            },
          ],
        },
        {
          margin: [0, 16, 0, 0],
          table: {
            widths: ['20%', '20%', '20%', '20%', '20%'],
            body: [
              [
                { text: 'Payment Terms', style: 'tableHeader', alignment: 'center' },
                { text: 'Delivery Terms', style: 'tableHeader', alignment: 'center' },
                { text: 'Shipping Date', style: 'tableHeader', alignment: 'center' },
                { text: 'POL', style: 'tableHeader', alignment: 'center' },
                { text: 'POD', style: 'tableHeader', alignment: 'center' },
              ],
              [
                { text: po.paymentTerms || '-', alignment: 'center', fontSize: 9 },
                { text: po.incoterms || '-', alignment: 'center', fontSize: 9 },
                { text: formatDateOrDash(po.expectedDeliveryDate), alignment: 'center', fontSize: 9 },
                { text: po.portOfLoading || '-', alignment: 'center', fontSize: 9 },
                { text: po.portOfDischarge || '-', alignment: 'center', fontSize: 9 },
              ],
            ],
          },
          layout: boxLayout,
        },
        {
          margin: [0, 16, 0, 0],
          table: {
            widths: ['*', 'auto', 'auto', 'auto', 'auto'],
            headerRows: 1,
            body: [
              [
                { text: 'Description', style: 'tableHeader' },
                { text: 'Unit', style: 'tableHeader', alignment: 'center' },
                { text: 'Quantity', style: 'tableHeader', alignment: 'center' },
                { text: 'Price', style: 'tableHeader', alignment: 'right' },
                { text: 'Amount', style: 'tableHeader', alignment: 'right' },
              ],
              ...po.items.map((item) => [
                { text: item.sku ? `[${item.sku}] ${item.productName}` : item.productName, fontSize: 9 },
                { text: 'Unit', alignment: 'center', fontSize: 9 },
                { text: Number(item.quantity || 0).toFixed(1), alignment: 'center', fontSize: 9 },
                { text: money(item.unitCost), alignment: 'right', fontSize: 9 },
                { text: money(Number(item.unitCost || 0) * Number(item.quantity || 0)), alignment: 'right', fontSize: 9 },
              ]),
              [
                { text: 'TOTAL', bold: true, colSpan: 2 },
                {},
                { text: po.items.reduce((s, i) => s + Number(i.quantity || 0), 0).toFixed(1), alignment: 'center', bold: true },
                { text: '' },
                { text: money(po.subtotal), alignment: 'right', bold: true },
              ],
            ],
          },
          layout: boxLayout,
        },
        {
          columns: [
            {
              width: '58%',
              table: {
                widths: ['*'],
                heights: [16, 70],
                body: [
                  [{ text: 'Terms & Conditions', bold: true, fontSize: 10 }],
                  [{ text: po.notes || '', fontSize: 9 }],
                ],
              },
              layout: boxLayout,
            },
            { width: '2%', text: '' },
            {
              width: '40%',
              table: {
                widths: ['60%', '40%'],
                body: [
                  [{ text: 'Total Net Amount', fontSize: 9 }, { text: money(po.subtotal), alignment: 'right', fontSize: 9 }],
                  [{ text: 'Total Tax Amount', fontSize: 9 }, { text: money(po.taxTotal), alignment: 'right', fontSize: 9 }],
                  [{ text: 'Shipping', fontSize: 9 }, { text: money(0), alignment: 'right', fontSize: 9 }],
                  [{ text: 'Grand Total', bold: true, fontSize: 10 }, { text: money(po.grandTotal), alignment: 'right', bold: true, fontSize: 10 }],
                ],
              },
              layout: boxLayout,
            },
          ],
        },
        {
          margin: [0, 60, 0, 0],
          columns: [
            {
              width: '40%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#333333' }] },
                { text: supplier?.name || '-', margin: [0, 4, 0, 0], fontSize: 9 },
              ],
            },
            { width: '20%', text: '' },
            {
              width: '40%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#333333' }] },
                { text: buyer.name, margin: [0, 4, 0, 0], fontSize: 9 },
              ],
            },
          ],
        },
      ],
      styles: {
        header: { fontSize: 18, bold: true },
        tableHeader: { fontSize: 9, bold: true, color: '#333333' },
      },
      defaultStyle: { fontSize: 9, color: '#333333' },
    };

    pdfMake
      .createPdf(
        dd,
        {},
        {
          Roboto: {
            normal: 'Roboto-Regular.ttf',
            bold: 'Roboto-Medium.ttf',
            italics: 'Roboto-Italic.ttf',
            bolditalics: 'Roboto-Italic.ttf',
          },
        },
        pdfFonts.pdfMake.vfs,
      )
      .open();
  }
}
